import Papa from 'papaparse';
import { supabase } from '../supabaseClient';

export class PayrollImporter {
  constructor() {
    this.onProgress = null;
    this.onComplete = null;
    this.onError = null;
    this.onValidationRequired = null;
  }

  // Set progress callback
  setProgressCallback(callback) {
    this.onProgress = callback;
  }

  // Set completion callback
  setCompleteCallback(callback) {
    this.onComplete = callback;
  }

  // Set error callback
  setErrorCallback(callback) {
    this.onError = callback;
  }

  // Set validation required callback
  setValidationCallback(callback) {
    this.onValidationRequired = callback;
  }

  // Clean currency values
  cleanCurrency(value) {
    if (!value || value === '' || value === null || value === undefined) {
      return null;
    }
    if (typeof value === 'string') {
      // Remove $ and commas
      const cleaned = value.replace(/[$,]/g, '');
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? null : parsed;
    }
    return parseFloat(value);
  }

  // Validate CSV structure
  validateCSV(data) {
    if (!data || data.length === 0) {
      throw new Error('CSV file is empty');
    }

    const requiredColumns = [
      'Employee Id',
      'First Name', 
      'Last Name',
      'Pay Date',
      'Total Hours',
      'Gross',
      'Deductions (EE)',
      'Deductions (ER)',
      'Taxes (EE)',
      'Taxes (ER)',
      'Net Payment'
    ];

    const headers = Object.keys(data[0]);
    const missingColumns = requiredColumns.filter(col => !headers.includes(col));
    
    if (missingColumns.length > 0) {
      throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
    }

    return true;
  }

  // Process CSV data
  processCSVData(rawData, company, payrollNumber) {
    const records = [];
    let skippedCount = 0;

    for (const row of rawData) {
      // Skip rows with empty Employee Id
      if (!row['Employee Id'] || row['Employee Id'].trim() === '') {
        continue;
      }

      // Get hours and gross pay to check for exclusion
      const totalHours = this.cleanCurrency(row['Total Hours']);
      const grossPay = this.cleanCurrency(row['Gross']);

      // Skip staff with 0 hours and 0 pay
      if ((totalHours === null || totalHours === 0) && (grossPay === null || grossPay === 0)) {
        skippedCount++;
        continue;
      }

      // Create record
      const record = {
        employee_id: row['Employee Id'].toString().trim(),
        first_name: row['First Name'] ? row['First Name'].toString().trim() : '',
        last_name: row['Last Name'] ? row['Last Name'].toString().trim() : '',
        total_hours: totalHours,
        gross_pay: grossPay,
        pay_date: row['Pay Date'] ? row['Pay Date'].toString().trim() : null,
        employee_deductions: this.cleanCurrency(row['Deductions (EE)']),
        employer_deductions: this.cleanCurrency(row['Deductions (ER)']),
        employee_taxes: this.cleanCurrency(row['Taxes (EE)']),
        employer_taxes: this.cleanCurrency(row['Taxes (ER)']),
        net_payment: this.cleanCurrency(row['Net Payment']),
        company: company,
        payroll_number: payrollNumber
      };

      records.push(record);
    }

    return { records, skippedCount };
  }

  // Validate employee IDs against employees table
  async validateEmployeeIds(records, company) {
    if (this.onProgress) {
      this.onProgress({ stage: 'validating', message: 'Validating employee records...' });
    }

    // Get all employees from database
    // First try to get all columns to see what's available
    const { data: allEmployees, error } = await supabase
      .from('employees')
      .select('*');

    if (error) {
      throw new Error(`Failed to fetch employees: ${error.message}`);
    }

    // Debug: Log the first employee record to see available columns
    if (allEmployees && allEmployees.length > 0) {
      console.log('Available employee columns:', Object.keys(allEmployees[0]));
      console.log('Sample employee record:', allEmployees[0]);
    }

    const validRecords = [];
    const unmatchedRecords = [];

    // Check each record
    for (const record of records) {
      const employeeId = record.employee_id;
      
      // Find matching employee based on company
      let matchingEmployee = null;
      
      if (company === 'KF') {
        matchingEmployee = allEmployees.find(emp => emp.employee_id_kf === employeeId);
      } else if (company === 'KOO') {
        matchingEmployee = allEmployees.find(emp => emp.employee_id_koo === employeeId);
      }

      if (matchingEmployee) {
        validRecords.push({
          ...record,
          matched_employee: matchingEmployee
        });
      } else {
        unmatchedRecords.push({
          ...record,
          csv_name: `${record.first_name} ${record.last_name}`.trim(),
          available_employees: allEmployees.map(emp => ({
            employee_number: emp.employee_number,
            employee_name: emp.employee_name || emp.name || emp.full_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Unknown',
            employee_id_kf: emp.employee_id_kf,
            employee_id_koo: emp.employee_id_koo
          }))
        });
      }
    }

    return {
      validRecords,
      unmatchedRecords,
      allEmployees
    };
  }

  // Update employee record with new employee ID
  async updateEmployeeId(employeeNumber, company, newEmployeeId) {
    const updateField = company === 'KF' ? 'employee_id_kf' : 'employee_id_koo';
    
    const { error } = await supabase
      .from('employees')
      .update({ [updateField]: newEmployeeId })
      .eq('employee_number', employeeNumber);

    if (error) {
      throw new Error(`Failed to update employee: ${error.message}`);
    }

    return true;
  }

  // Clear existing company data for specific payroll period
  async clearExistingData(company, payrollNumber) {
    try {
      const { error } = await supabase
        .from('payroll_dynamics')
        .delete()
        .eq('company', company)
        .eq('payroll_number', payrollNumber);
      
      if (error) {
        console.warn('Could not clear existing data:', error);
        return false;
      }
      return true;
    } catch (error) {
      console.warn('Could not clear existing data:', error);
      return false;
    }
  }

  // Insert records in batches
  async insertRecords(records) {
    const batchSize = 50;
    let totalInserted = 0;
    const totalBatches = Math.ceil(records.length / batchSize);

    for (let i = 0; i < records.length; i += batchSize) {
      const batch = records.slice(i, i + batchSize);
      const batchNumber = Math.floor(i / batchSize) + 1;

      try {
        const { error } = await supabase
          .from('payroll_dynamics')
          .insert(batch);
        
        if (error) {
          throw error;
        }
        
        totalInserted += batch.length;

        // Report progress
        if (this.onProgress) {
          this.onProgress({
            processed: totalInserted,
            total: records.length,
            batch: batchNumber,
            totalBatches: totalBatches,
            percentage: Math.round((totalInserted / records.length) * 100)
          });
        }
      } catch (error) {
        throw new Error(`Failed to insert batch ${batchNumber}: ${error.message}`);
      }
    }

    return totalInserted;
  }

  // Main import function
  async importCSV(file, company, payrollNumber) {
    try {
      // Validate payroll number
      if (!payrollNumber || typeof payrollNumber !== 'number') {
        throw new Error('Valid payroll number is required');
      }

      // Report start
      if (this.onProgress) {
        this.onProgress({ stage: 'parsing', message: 'Parsing CSV file...' });
      }

      // Parse CSV without header first to handle metadata rows
      const parseResult = await new Promise((resolve, reject) => {
        Papa.parse(file, {
          header: false,
          skipEmptyLines: true,
          complete: resolve,
          error: reject
        });
      });

      if (parseResult.errors && parseResult.errors.length > 0) {
        const nonCriticalErrors = parseResult.errors.filter(e => 
          !e.message.includes('Too many fields') && 
          !e.message.includes('expected 1 fields')
        );
        if (nonCriticalErrors.length > 0) {
          throw new Error(`CSV parsing errors: ${nonCriticalErrors.map(e => e.message).join(', ')}`);
        }
      }

      // Find the header row (usually contains "Employee Id")
      let headerRowIndex = -1;
      for (let i = 0; i < parseResult.data.length; i++) {
        const row = parseResult.data[i];
        if (row && row.length > 1 && row[0] === 'Employee Id') {
          headerRowIndex = i;
          break;
        }
      }

      if (headerRowIndex === -1) {
        throw new Error('Could not find header row with "Employee Id" column');
      }

      // Extract headers and data
      const headers = parseResult.data[headerRowIndex];
      const rawDataRows = parseResult.data.slice(headerRowIndex + 1);

      // Convert to objects with headers
      const dataRows = rawDataRows
        .filter(row => row && row.length > 1) // Filter out empty rows
        .map(row => {
          const obj = {};
          headers.forEach((header, index) => {
            obj[header] = row[index] || '';
          });
          return obj;
        });

      // Validate CSV structure
      this.validateCSV(dataRows);

      // Process data
      if (this.onProgress) {
        this.onProgress({ stage: 'processing', message: 'Processing CSV data...' });
      }

      const { records, skippedCount } = this.processCSVData(dataRows, company, payrollNumber);

      if (records.length === 0) {
        throw new Error('No valid records found in CSV file');
      }

      // Validate employee IDs
      const validation = await this.validateEmployeeIds(records, company);

      // If there are unmatched records, trigger validation callback
      if (validation.unmatchedRecords.length > 0 && this.onValidationRequired) {
        const validationResult = await this.onValidationRequired({
          validRecords: validation.validRecords,
          unmatchedRecords: validation.unmatchedRecords,
          company: company
        });

        // If validation was cancelled, stop import
        if (!validationResult || validationResult.cancelled) {
          throw new Error('Import cancelled by user');
        }

        // Update employee records with new mappings
        if (validationResult.mappings && validationResult.mappings.length > 0) {
          if (this.onProgress) {
            this.onProgress({ stage: 'updating', message: 'Updating employee records...' });
          }

          for (const mapping of validationResult.mappings) {
            await this.updateEmployeeId(mapping.employeeNumber, company, mapping.newEmployeeId);
          }
        }

        // Re-validate after updates
        const revalidation = await this.validateEmployeeIds(records, company);
        if (revalidation.unmatchedRecords.length > 0) {
          throw new Error(`Still have ${revalidation.unmatchedRecords.length} unmatched records after mapping`);
        }
      }

      // Clear existing data
      if (this.onProgress) {
        this.onProgress({ stage: 'clearing', message: `Clearing existing ${company} data for payroll ${payrollNumber}...` });
      }

      await this.clearExistingData(company, payrollNumber);

      // Insert new data
      if (this.onProgress) {
        this.onProgress({ stage: 'inserting', message: 'Inserting records...' });
      }

      const totalInserted = await this.insertRecords(records);

      // Complete
      const result = {
        success: true,
        totalInserted,
        skippedCount,
        company,
        payrollNumber,
        message: `Successfully imported ${totalInserted} records for ${company} payroll #${payrollNumber}. Skipped ${skippedCount} records with 0 hours/pay.`
      };

      if (this.onComplete) {
        this.onComplete(result);
      }

      return result;

    } catch (error) {
      const errorResult = {
        success: false,
        error: error.message,
        company,
        payrollNumber
      };

      if (this.onError) {
        this.onError(errorResult);
      }

      throw error;
    }
  }
}

export default PayrollImporter;